import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import { useCourseEnrollment } from "@/hooks/use-course-enrollment";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import CheckoutDialog from "@/components/blueprint/CheckoutDialog";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import AccordionCurriculumSection from "@/components/blueprint/AccordionCurriculumSection";
import BlueprintHero from "@/components/blueprint/BlueprintHero";
import BlueprintStats from "@/components/blueprint/BlueprintStats";
import BlueprintPricing from "@/components/blueprint/BlueprintPricing";
import BlueprintFinalCta from "@/components/blueprint/BlueprintFinalCta";
import BookPreviewSection from "@/components/blueprint/BookPreviewSection";
import CourseOverview from "@/components/blueprint/CourseOverview";
import DetailedCurriculumSection from "@/components/blueprint/DetailedCurriculumSection";

const BlueprintPage: React.FC = () => {
  const navigate = useNavigate();
  const pricingSectionRef = useRef<HTMLDivElement>(null);
  const {
    isEnrolled,
    loading,
    introLessonId,
    isCourseAvailable,
    formatReleaseDate,
  } = useCourseEnrollment();

  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutAttempts, setCheckoutAttempts] = useState(0);
  
  const handleWatchIntroClick = () => {
    if (introLessonId) {
      navigate(`/lesson/${introLessonId}`);
    }
  };
  
  // Function to scroll to pricing section
  const handleScrollToPricing = () => {
    pricingSectionRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };
  
  // For backward compatibility - preserving the form-based checkout flow
  const handleCheckoutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@') || !email.includes('.')) {
      setEmailError("Please enter a valid email address");
      return;
    }
    
    setEmailError("");
    setCheckoutError(null);
    
    // We can remove this implementation as it's now handled by the useStripeCheckout hook
    setIsCheckoutDialogOpen(false);
  };

  // Check if the user just completed a successful purchase
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const canceled = urlParams.get('canceled') === 'true';
    
    // Remove URL parameters regardless of outcome, but after we've processed them
    const cleanupUrl = () => {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    };
    
    if (sessionId) {
      // Show success toast and redirect to the success page
      toast({
        title: "Payment successful!",
        description: "Thank you for enrolling in the course!",
        variant: "default",
      });
      
      cleanupUrl();
      navigate('/purchase-success');
    } else if (canceled) {
      // Handle the case where the user canceled the checkout
      toast({
        title: "Checkout canceled",
        description: "You've canceled the checkout process. Feel free to try again when you're ready.",
        variant: "default",
      });
      
      cleanupUrl();
    }
  }, [navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-techne-purple animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container max-w-6xl py-12">
        {/* Hero Section */}
        <BlueprintHero
          isEnrolled={isEnrolled}
          isCourseAvailable={isCourseAvailable}
          introLessonId={introLessonId}
          formatReleaseDate={formatReleaseDate}
          handleWatchIntroClick={handleWatchIntroClick}
          onEnrollClick={() => {}}
          handleScrollToPricing={handleScrollToPricing}
        />
        
        {/* Stats Section - Moved above Course Overview */}
        <BlueprintStats />
        
        {/* Course Overview Section */}
        <CourseOverview handleScrollToPricing={handleScrollToPricing} />
        
        {/* Book Preview Section */}
        <BookPreviewSection />
        
        <Separator className="my-16" />
        
        {/* Accordion Curriculum Section */}
        <AccordionCurriculumSection />
        
        {/* Pricing Section */}
        <div id="pricing" ref={pricingSectionRef}>
          <BlueprintPricing />
        </div>
        
        {/* Final CTA Section */}
        <BlueprintFinalCta
          isEnrolled={isEnrolled}
          isCourseAvailable={isCourseAvailable}
          formatReleaseDate={formatReleaseDate}
          handleWatchIntroClick={handleWatchIntroClick}
          onEnrollClick={() => {}}
          handleScrollToPricing={handleScrollToPricing}
        />
      </div>

      {/* Email dialog preserved for legacy support */}
      <CheckoutDialog
        isOpen={isCheckoutDialogOpen}
        onOpenChange={setIsCheckoutDialogOpen}
        email={email}
        setEmail={setEmail}
        emailError={emailError}
        formatReleaseDate={formatReleaseDate}
        onSubmit={handleCheckoutSubmit}
        isSubmitting={false}
      />
    </Layout>
  );
};

export default BlueprintPage;
